import { toRaw } from 'vue';

import parent from '@js/dom/parent';

export const ORIGIN = '*';
export const PREFIX = 'credere';

/*
 * OS DOIS SENTIDOS DE UMA MENSAGEM, e é o sufixo que os separa.
 *
 * `<model>:<verbo>` é UM FATO: "este registro foi lido / criado / mudou". Quem recebe decide
 * se faz algo - e `vehicle:update` é o exemplo de fazer nada de propósito (o host declara a
 * ausência do handler em comentário, em `vehicles/index.html.erb`).
 *
 * `<model>:<verbo>:action` é UM PEDIDO: "leve-me a algum lugar". Só ele carrega
 * `{ action, settings }` - o `action` é o nome lógico do destino (`<plural>/read`,
 * `vehicles/create`, `misc/back`) e o `settings` traz os ids para o host montar a URL. O host
 * faz `switch` no `action`; o tipo é o CANAL do modelo, não um destino - por isso
 * `simulation:read:action` já carrega `customers/form` e `misc/back`, que não são leituras, e
 * por isso as ações de página do estoque viajam em `vehicle:read:action`.
 *
 * A DISTINÇÃO NÃO É ACADÊMICA: `simulation:read` e `customer:read` têm dois emissores cada -
 * o formulário, que diz "carreguei", e a listagem, que dizia "abra". Com um tipo só, a página
 * que hospeda os dois não tinha como saber qual dos dois chegou (medido em 2026-08-29:
 * `public_simulator/customer.html.erb` só escapa porque não declara o handler).
 *
 * O `:action` NÃO substitui o `<a href>` da linha: o anchor é o que faz cmd+clique, clique do
 * meio e "abrir em nova aba". Por isso o `settings` de listagem não leva `_blank` - o do inbox
 * leva porque lá não há anchor nenhum, é um botão dentro de uma bolha.
 */
export const TYPES = [
  'scroll',
  'load',
  'resize',
  'token',

  'customer:read',
  'customer:read:action',
  'customer:create',
  'customer:update',

  'public_simulator_setting:read',
  'public_simulator_setting:create',
  'public_simulator_setting:update',
  'public_simulator_setting:find',
  'public_simulator_setting:contact',
  'public_simulator_setting:action',

  'lead:create',
  'lead:update',

  'simulation:create',

  'simulation:read',
  'simulation:read:processing',
  'simulation:read:action',

  'proposal:read',
  'proposal:read:action',
  'proposal:create',
  'proposal:create:failure',
  'proposal_attempt:create',
  'proposal_attempt:update',

  'vehicle:read',
  'vehicle:read:action',
  'vehicle:create',
  'vehicle:update',
  'vehicle:import',
  'vehicle:list',
  'vehicle:find',
  'vehicle:showroom:action',

  'vehicle_model:read',
  'vehicle_model:read:action',
  'vehicle_model:create',
  'vehicle_model:update',
  'vehicle_model:list',

  'report:score:action',

  'bank_commission_rules:read',
  'bank_commission_rules:update',

  'bank_ranking:read',
  'bank_ranking:update',

  'public_simulator_distribution:read',
  'public_simulator_distribution:update',
  'public_simulator_distribution:action',

  'public_simulator_connection:update',

  'public_simulator_automation:read',
  'public_simulator_automation:read:action',
  'public_simulator_automation:create',
  'public_simulator_automation:update',
  'public_simulator_automation:delete',

  'public_simulator_notification:read',
  'public_simulator_notification:read:action',
  'public_simulator_notification:create',
  'public_simulator_notification:update',
  'public_simulator_notification:delete',

  'public_simulator_question:read',
  'public_simulator_question:read:action',
  'public_simulator_question:create',
  'public_simulator_question:update',
  'public_simulator_question:delete',

  'public_simulator_conversation:filter',

  'store:read',
  'store:read:action',

  /*
   * A LINHA DE UM CONVITE JÁ ACEITO LEVA AO PERFIL DE QUEM SE CADASTROU, e é a ÚNICA
   * mensagem daquela listagem (embed#22): apagar um convite pendente é deste app inteiro -
   * modal de confirmação, `DELETE` e a lista recarregada no lugar -, e o host não é avisado
   * porque não há o que ele faça.
   */
  'user_invitation:read:action',
  'user_invitation:delete',

  'system_alert:read',
  'system_alert:read:action',
  'system_alert:create',
  'system_alert:update',

  // As três coleções comerciais. O formulário de cada uma segue na pwa, então a linha da
  // listagem só pede ao host que o abra (embed#22).
  'financing_return_rule:read',
  'financing_return_rule:read:action',
  'vehicle_price:read',
  'vehicle_price:read:action',
  'financing_product_rule:read',
  'financing_product_rule:read:action',

  /*
   * A OPÇÃO ESCOLHIDA NUM DOS TRÊS MENUS DE `/settings`. Um tipo só, e não um por destino:
   * os destinos dali não são de um modelo - a conta, o usuário, o catálogo de veículos e as
   * telas de `/settings` são coisas diferentes -, e o que eles têm em comum é justamente
   * serem uma opção de menu. O `settings` leva a CHAVE do item, que o host resolve na mesma
   * tabela com que publicou as URLs (`views/Settings/javascripts/menus.js`).
   */
  'setting:read:action',

  'onboarding:subscription:action',
].reduce((reduced, type) => {
  reduced[type] = {
    prefixed: `${PREFIX}:${type}`,
    constant: type.replace(/:/g, '_').toUpperCase(),
  };

  return reduced;
}, {});

/*
 * O PAYLOAD SAI DA REATIVIDADE ANTES DE ATRAVESSAR, e é a fronteira que faz isto, não cada
 * tela.
 *
 * `postMessage` serializa por structured clone - inclusive para a MESMA janela, que é o
 * caso quando o host é o `web` -, e structured clone não sabe clonar um `Proxy`: cai em
 * `DataCloneError: #<Object> could not be cloned` e a mensagem simplesmente não sai. Todo
 * objeto que uma tela tem à mão para mandar é reativo - o item de uma listagem vem do
 * `items` da coleção, a entidade vem do formulário -, então o defeito não é de uma tela: é
 * do padrão, e reaparece na próxima que emitir.
 *
 * `toRaw` DESCENDO, e um nível não basta. Quando o valor JÁ É o proxy, um nível resolveria:
 * `toRaw` devolve o objeto original e as propriedades de um objeto original são os valores
 * originais (a reatividade do Vue é profunda mas PREGUIÇOSA - o proxy do filho só nasce ao
 * acessar a propriedade do proxy do pai). Só que o `:action` não manda o registro: ele manda
 * `{ action, settings }`, e o `settings` é um objeto MONTADO À MÃO pela tela, com valores
 * tirados de dentro da coleção reativa. O objeto de fora é literal, o `toRaw` dele não faz
 * nada, e o proxy que estava numa das chaves atravessa intacto até o structured clone.
 *
 * Foi o que derrubou `simulations/read`: o `related` do `settings` é o
 * `related_simulations_uuids` do item da listagem, ou seja, um array reativo.
 *
 * PLANO E ARRAY SÓ. O que se reconstrói é objeto literal e array; `Date`, `File` e `Blob`
 * passam inteiros, porque o structured clone dá conta deles e refazê-los os quebraria. Sem
 * guarda de ciclo de propósito: o que entra aqui é resposta de API ou id de linha, e um
 * grafo com volta nunca chegou - se chegar, o sintoma é pilha estourada e não silêncio.
 *
 * Isto NÃO cobre função dentro do payload, que também não se clona - mas função em mensagem
 * seria um callback atravessando um limite de janela, que é outro erro e tem outro nome.
 */
function raw(value) {
  const unwrapped = toRaw(value);

  if (Array.isArray(unwrapped)) {
    return unwrapped.map(raw);
  }

  if (unwrapped?.constructor === Object) {
    return Object.keys(unwrapped).reduce((reduced, key) => {
      reduced[key] = raw(unwrapped[key]);

      return reduced;
    }, {});
  }

  return unwrapped;
}

/*
 * O HOST DECLAROU CALLBACK PARA ESTE TIPO?
 *
 * O `initialize.js` publica em `CREDERE.listening` as chaves de `CREDERE.callbacks`, e é
 * assim que uma tela pergunta se vale contar com o host - `views/Customers/Form.vue` já
 * pergunta, para saber se pode delegar o aviso de falha.
 *
 * A pergunta chega pelo NOME DA CONSTANTE, que é como as telas se referem a uma mensagem
 * (`MESSAGE.PROPOSAL_CREATE`); a lista tem os tipos, com ou sem o prefixo, porque o host
 * pode declarar de qualquer das duas formas e o `initialize.js` aceita as duas.
 *
 * Tipo que não existe responde `false`: nada foi declarado para o que não se pode emitir.
 */
const CONSTANTS = Object.keys(TYPES).reduce((reduced, type) => {
  reduced[TYPES[type].constant] = type;

  return reduced;
}, {});

export function listened(constant) {
  const type = CONSTANTS[constant];

  if (!type) {
    return false;
  }

  const listening = window.CREDERE.listening || [];

  return listening.includes(type) || listening.includes(TYPES[type].prefixed);
}

function message(type, payload = {}) {
  if (!TYPES[type]) {
    // eslint-disable-next-line no-console
    console.error(`MESSAGE TYPE ${type} NOT FOUND`);
  }

  parent().postMessage({
    type: TYPES[type].prefixed,
    payload: raw(payload),
  }, ORIGIN);
}

export default Object.keys(TYPES).reduce((reduced, type) => {
  reduced[TYPES[type].constant] = (payload) => message(type, payload);

  return reduced;
}, {});
